import { useState, useMemo, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import moment from 'moment'

import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { groupBy, sortBy } from 'lib/array/arrayUtils'

import CruiseDepartureFilters from './CruiseDepartureFilters'
import NeedHelp from '../NeedHelp'
import CruiseDepartureTile from './CruiseDepartureTile/CruiseDepartureTile'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

export type DeparturesByDate = {
  [date: string]: Array<App.CruiseDepartureDetails>
}

interface Props {
  offer: App.CruiseOffer
  departuresByDate: DeparturesByDate
  hasValidDepartures: boolean
  onHandleSelectDeparture: (departure: App.CruiseDepartureDetails) => void
  offerPageOccupancies: Array<App.Occupants>
  setOfferPageOccupancies: (occupancies: Array<App.Occupants>) => void
  onOfferPageOccupancyApplied?: () => void
  isCruiseOfferTravellerPickerVariant: boolean
}

function CruiseOfferPageDepartures({
  offer,
  departuresByDate,
  hasValidDepartures,
  onHandleSelectDeparture,
  offerPageOccupancies,
  setOfferPageOccupancies,
  onOfferPageOccupancyApplied,
  isCruiseOfferTravellerPickerVariant,
}: Props) {
  const { lowestDeparturePricesByCategory } = offer
  const dateFormatter = useIntlDateFormatter()
  const [selectedYear, setSelectedYear] = useState<number | undefined>()
  const [sort, setSort] = useState<string | undefined>()

  const referenceDepartureDateForOccupancy = useMemo(() => {
    const allDepartures = Object.values(departuresByDate).flat()
    return allDepartures[0]?.departureDate
  }, [departuresByDate])

  const getPricesFromDeparture = useCallback((departure: App.CruiseDepartureDetails) => {
    return Object.values(lowestDeparturePricesByCategory[departure.id] || {})
      .map((price) => price.total)
  }, [lowestDeparturePricesByCategory])

  const yearOptions = useMemo(
    () => {
      const years = Object.keys(departuresByDate).map(date => Number(date.split('-')[0]))
      const uniqueYears = Array.from(new Set(years))

      return sortBy(uniqueYears, year => year, 'asc')
    },
    [departuresByDate],
  )

  const departureList = useMemo(() => (
    Object.values(departuresByDate)
      .flat()
      .map((departure) => {
        const prices = getPricesFromDeparture(departure)
        const lowestPrice = Math.min(...prices)
        const highestPrice = Math.max(...prices)

        const dateMoment = moment(departure.departureDate)

        const sortingData = {
          lowestPrice,
          highestPrice,
          dateUnix: dateMoment.unix(),
          year: dateMoment.year(),
        }

        return {
          dateLabel: dateFormatter(dateMoment, INTL_LONG_MONTH_LONG_YEAR),
          departure,
          sortingData,
        }
      })
  ), [dateFormatter, departuresByDate, getPricesFromDeparture])

  const departureCountByYear = useMemo(() => {
    const acc: Record<number, number> = {}
    for (const { sortingData } of departureList) {
      acc[sortingData.year] = (acc[sortingData.year] ?? 0) + 1
    }
    return acc
  }, [departureList])

  const filteredAndSortedDepartureList = useMemo(() => (
    departureList
      .filter(({ sortingData }) => !selectedYear || sortingData.year === selectedYear)
      .sort((a, b) => {
        if (sort === 'price-asc') {
          return a.sortingData.lowestPrice - b.sortingData.lowestPrice
        }

        if (sort === 'price-desc') {
          return b.sortingData.highestPrice - a.sortingData.highestPrice
        }

        return a.sortingData.dateUnix - b.sortingData.dateUnix
      })
  ), [departureList, selectedYear, sort])

  const departuresByMonth = useMemo(
    () => Array.from(groupBy(filteredAndSortedDepartureList, ({ dateLabel }) => dateLabel).entries()),
    [filteredAndSortedDepartureList],
  )

  const hasMultipleDepartures = departureList.length > 1

  return <LayoutContainer>
    <ScrollAnchor id="departures-anchor" />

    <Sheet padding="32 0">
      <Group direction="vertical" gap={40}>
        <Group direction="vertical" gap={24}>
          <Heading variant="heading3" align="center" className="section-title">
            <FormattedMessage
              id="A51U7aAfppFcGWSpIvSj"
              defaultMessage="Dates & Pricing"
              description="Section heading for cruise departure dates and pricing"
            />
          </Heading>

          <Divider kind="secondary" />
        </Group>

        <CruiseDepartureFilters
          sort={sort}
          yearOptions={yearOptions}
          selectedYear={selectedYear}
          totalDepartureCount={departureList.length}
          departureCountByYear={departureCountByYear}
          setSort={setSort}
          setSelectedYear={setSelectedYear}
          ship={offer.ship}
          offerPageOccupancies={offerPageOccupancies}
          setOfferPageOccupancies={setOfferPageOccupancies}
          onOfferPageOccupancyApplied={onOfferPageOccupancyApplied}
          referenceDepartureDateForOccupancy={referenceDepartureDateForOccupancy ?? ''}
          isCruiseOfferTravellerPickerVariant={isCruiseOfferTravellerPickerVariant}
          hasMultipleDepartures={hasMultipleDepartures}
        />

        <Group direction="vertical" gap={36}>
          {departuresByMonth.map(([dateLabel, departures]) => (
            <VerticalSpacer gap={20} key={dateLabel}>
              <VerticalSpacer gap={16}>
                <BodyText variant="large" weight="bold" align="start">{dateLabel}</BodyText>
                <CSSBreakpoint only="mobile">
                  <Divider kind="secondary" direction="horizontal" />
                </CSSBreakpoint>
              </VerticalSpacer>

              {departures.map(({ departure }) => (
                <CruiseDepartureTile
                  offer={offer}
                  key={departure.id}
                  departure={departure}
                  onHandleDepartureModal={onHandleSelectDeparture}
                />
              ))}
            </VerticalSpacer>
          ))}

          {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
          {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
          {hasValidDepartures && <NeedHelp />}
        </Group>
      </Group>
    </Sheet>
  </LayoutContainer>
}

export default CruiseOfferPageDepartures
